import { loadCatalog } from '@/i18n/lingui';
import { ClientStyleProvider } from '@/theme/emotion';
import { i18n } from '@lingui/core';
import { detect, fromHtmlTag } from '@lingui/detect-locale';
import { I18nProvider } from '@lingui/react';
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { DEFAULT_LOCALE } from './constants/app';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    attachStacktrace: true,
    tunnel: '/zone',
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      // Replay is only available in the client
      Sentry.replayIntegration({
        // Additional SDK configuration goes in here, for example:
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.breadcrumbsIntegration(),
      Sentry.browserApiErrorsIntegration(),
      Sentry.globalHandlersIntegration(),
      Sentry.linkedErrorsIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.httpContextIntegration(),
      Sentry.contextLinesIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.moduleMetadataIntegration(),
      Sentry.reportingObserverIntegration(),
    ],

    // Set tgamesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // We recommend adjusting this value in production
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#tgames-sample-rate
    tracesSampleRate: 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const hydrate = async () => {
  const locale = detect(fromHtmlTag('lang')) || DEFAULT_LOCALE;

  await loadCatalog(locale);

  React.startTransition(() => {
    ReactDOM.hydrateRoot(
      document,
      <I18nProvider i18n={i18n}>
        <ClientStyleProvider>
          <RemixBrowser />
        </ClientStyleProvider>
      </I18nProvider>
    );
  });
};

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  setTimeout(hydrate, 1);
}
